import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  deleteDocument,
  getDownloadUrl,
  type DeleteDocumentRequest,
  type DownloadUrlRequest,
} from "@/features/documents/api/documents.api"
import {
  uploadDocument,
  type UploadDocumentInput,
} from "@/features/documents/lib/upload-helpers"
import { extractApiErrorMessage } from "@/lib/api/client"

export function useUploadDocument(options?: {
  invalidateKeys?: readonly (readonly unknown[])[]
  onSuccess?: () => void
}) {
  const { t } = useTranslation("documents")
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: UploadDocumentInput) => uploadDocument(input),
    onSuccess: () => {
      toast.success(t("toast.uploaded"))
      options?.invalidateKeys?.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key as readonly unknown[] }),
      )
      options?.onSuccess?.()
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error) || t("errors.uploadFailed"))
    },
  })
}

export function useDeleteDocument(options?: {
  invalidateKeys?: readonly (readonly unknown[])[]
  onSuccess?: () => void
}) {
  const { t } = useTranslation("documents")
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: DeleteDocumentRequest) => deleteDocument(input),
    onSuccess: () => {
      toast.success(t("toast.deleted"))
      options?.invalidateKeys?.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key as readonly unknown[] }),
      )
      options?.onSuccess?.()
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error) || t("errors.deleteFailed"))
    },
  })
}

export function useDownloadDocument() {
  const { t } = useTranslation("documents")

  async function download(request: DownloadUrlRequest, suggestedName?: string) {
    try {
      const { url } = await getDownloadUrl(request)
      const a = document.createElement("a")
      a.href = url
      a.target = "_blank"
      a.rel = "noopener noreferrer"
      if (suggestedName) a.download = suggestedName
      document.body.appendChild(a)
      a.click()
      a.remove()
    } catch (error) {
      toast.error(extractApiErrorMessage(error) || t("errors.downloadFailed"))
    }
  }

  return { download }
}
